import { time } from 'echarts'
import { currencyFormatter } from '~/utils/currency'

export function nullFormatter(value) {
  return value
}

export function tooltipNullFormatter({ value }) {
  return value
}

export function tooltipMonthlyDateFormatter({ value }) {
  return time.format(value, '{MMM} {yyyy}', true)
}

export function tooltipDailyDateFormatter({ value }) {
  return time.format(value, '{MMM} {d} {yyyy}', true)
}

export function tooltipFormatter(format) {
  if (format === 'none') {
    return tooltipNullFormatter
  }
  return format === 'monthly' ? tooltipMonthlyDateFormatter : tooltipDailyDateFormatter
}

export function xAxisMonthlyDateFormatter(value) {
  return time.format(value, '{MMM} {yyyy}', true)
}

export function xAxisDailyDateFormatter(value) {
  return time.format(value, '{MMM} {d} {yyyy}', true)
}

export function formatDateWithDayAndHour(value) {
  return time.format(value, '{MMM} {d} {yyyy} {HH}:{mm}', true)
}

export function formatTimeWithWeekDay(value) {
  return time.format(value, '{ee}, {MMM} {d} {yyyy} {HH}:{mm}', true)
}

export function formatHourAndMinute(value) {
  return time.format(value, '{HH}:{mm}', true)
}

export function xAxisFormatter(format) {
  if (format === 'none') {
    return nullFormatter
  }
  return format === 'monthly' ? xAxisMonthlyDateFormatter : xAxisDailyDateFormatter
}

export function yAxisFormatter(currency, formatter) {
  let formatterFn

  if (formatter === 'currency') {
    formatterFn = currencyFormatter(currency, 'compact')
  } else if (formatter === 'percentage') {
    formatterFn = percentageFormatter
  } else {
    formatterFn = nullFormatter
  }

  return (value) => formatterFn(value)
}

export function labelFormatter(currency, formatter) {
  const formatterFn = resolveFormatter(formatter, currency)

  return (value) => {
    const {
      value: [, y],
    } = value
    return formatterFn(y)
  }
}

export function labelFormatterWithPrecision(currency) {
  const formatterFn = currencyFormatter(currency, 'withPrecision')

  return (value) => {
    const {
      value: [, y],
    } = value
    return formatterFn(y)
  }
}

export function labelFormatterWithUnit(unit) {
  return (value) => {
    const {
      value: [, y],
    } = value
    return `${Math.round(y)} ${unit}`
  }
}

export function yAxisFormatterWithUnit(unit) {
  return (value) => {
    return `${Math.round(value)} ${unit}`
  }
}

export function formatNumber(value) {
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 4 }).format(value)
}

export function formatBytes(bytes) {
  if (bytes < 1024) {
    // return bytes if less than 1 KiB
    return `${bytes} Bytes`
  } else if (bytes < 1024 * 1024) {
    // Convert to KiB if less than 1 MiB
    return `${(bytes / 1024).toFixed(2)} KiB`
  } else if (bytes < 1024 * 1024 * 1024) {
    // Convert to MiB if less than 1 GiB
    return `${(bytes / (1024 * 1024)).toFixed(2)} MiB`
  } else {
    // Convert to GiB
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GiB`
  }
}

export function formatGigabytes(gib) {
  if (gib < 1) {
    return `${gib.toFixed(2)} GiB`
  }

  const units = ['GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  const exponent = Math.floor(Math.log(gib) / Math.log(1024))
  const unit = units[exponent]
  const value = (gib / 1024 ** exponent).toFixed(2)

  return `${value} ${unit}`
}

export const rawValueFormatter = (value) => {
  return value
}
export const percentageFormatter = (value) => {
  return `${value}%`
}

export function resolveFormatter(formatter, currency) {
  switch (formatter) {
    case 'currency':
      return currencyFormatter(currency)
    case 'bytes':
      return formatBytes
    case 'gigabytes':
      return formatGigabytes
    case 'percentage':
      return percentageFormatter
    default:
      return nullFormatter
  }
}

export function pieOptionLabelFormatter(formatter, currency) {
  if (formatter === 'percentage') {
    return resolveFormatter('none', currency)
  }

  return resolveFormatter(formatter, currency)
}
